import { dataServiceBaseUrl } from "@/data-access/core/constants";
import { parseGoogleFontsStylesheetUrl } from "@/lib/template-studio/brandAssets";
import { templateStudioCatalog } from "@/lib/template-studio/catalog";
import { formatTemplateStudioComponentLabel } from "@/lib/template-studio/templateStudioComponentDisplay";
import { templateStudioRendererComponentTypes } from "@/lib/template-studio/templateStudioRendererRegistry";
import { isChartHexColor } from "@/lib/utils/chartUtils";
import type {
  TemplateLayoutSpec,
  TemplateLayoutTheme,
} from "@/types/template-layout/templateLayout.types";
import { templateHeraldSlotContentTypes } from "@/types/template-layout/templateSlotContent.types";

export interface TemplateStudioTurn {
  id: string;
  text: string;
  /** The message as Herald received it, with pasted image links swapped for their stored URLs. */
  request?: string;
  /** Uploaded images attached to this message, shown above the user bubble. */
  images?: { url: string; name: string }[];
  summary?: string;
  reply?: string;
  /** Template Studio's own notes, such as the banner size tip; never sent back to Herald. */
  notes?: { id: string; text: string; cropImageSrc?: string }[];
  /** The image the chat cropped or resized for this message, so it can be undone. */
  imageEdit?: {
    /** The edited copy. */
    src: string;
    previousSrc: string;
    banner: boolean;
    undone?: boolean;
  };
}

/** Keeps every prop name, including future catalog props; full values remain in the compiled spec. */
const describeTemplateStudioElements = (spec: TemplateLayoutSpec | null) => {
  const elements: {
    id: string;
    component: string;
    propPreviews: Record<string, string>;
  }[] = [];
  const pending = spec ? [spec.root] : [];
  let remainingCharacters = 8_000;
  for (
    let id = pending.pop();
    spec && id !== undefined && elements.length < 40;
    id = pending.pop()
  ) {
    const element = spec.elements[id];
    if (!element) continue;
    const description = {
      id,
      component: element.type,
      propPreviews: Object.fromEntries(
        Object.entries(element.props ?? {})
          .filter(([, value]) => value !== undefined)
          .map(([name, value]) => [name, JSON.stringify(value).slice(0, 120)]),
      ),
    };
    remainingCharacters -= JSON.stringify(description).length;
    if (remainingCharacters < 0) break;
    elements.push(description);
    pending.push(...[...(element.children ?? [])].reverse());
  }
  return elements;
};

/** Captures what the user can see, including renderer-owned order and computed typography. */
export const buildTemplateStudioPreviewContext = ({
  preview,
  spec,
  theme,
  turns,
}: {
  preview: HTMLElement | null;
  spec: TemplateLayoutSpec | null;
  theme: Partial<TemplateLayoutTheme>;
  /** Recent turns for Herald. Omit when a stored conversation sends the history. */
  turns?: TemplateStudioTurn[];
}): string => {
  const visibleNodes = Array.from(
    preview?.querySelectorAll<HTMLElement>(
      "header, h1, h2, h3, p, nav, article, footer",
    ) ?? [],
  ).filter((node) => node.closest('[aria-hidden="true"]') === null);
  const landmarks = visibleNodes.filter((node) =>
    node.matches("header, h1, h2, nav, footer"),
  );
  const selectedNodes = new Set(
    [
      ...landmarks,
      ...visibleNodes.filter((node) => !landmarks.includes(node)),
    ].slice(0, 40),
  );
  const authoredOrder = describeTemplateStudioElements(spec);
  const context = {
    authoredOrder,
    authoredOrderTruncated:
      authoredOrder.length < Object.keys(spec?.elements ?? {}).length,
    theme: {
      primaryColor: theme.primaryColor,
      fontFamily: theme.fontFamily?.slice(0, 100),
      fontStylesheetUrl: theme.fontStylesheetUrl?.slice(0, 2000),
      logoAssetId: theme.logoAssetId?.slice(0, 200),
    },
    ...(turns
      ? {
          completedTurns: turns.slice(-4).map((turn) => ({
            request: (turn.request ?? turn.text).slice(0, 600),
            outcome:
              turn.summary?.slice(0, 1000) ?? "No successful update recorded.",
            ...(turn.reply ? { reply: turn.reply.slice(0, 1200) } : {}),
          })),
        }
      : {}),
    renderedOrderTruncated: visibleNodes.length > selectedNodes.size,
    renderedOrder: visibleNodes
      .filter((node) => selectedNodes.has(node))
      .map((node) => {
        const style = window.getComputedStyle(node);
        return {
          tag: node.tagName.toLowerCase(),
          text: node.textContent?.trim().slice(0, 120),
          fontFamily: style.fontFamily.slice(0, 100),
          fontSize: style.fontSize,
          fontWeight: style.fontWeight,
          color: style.color,
          backgroundColor: style.backgroundColor,
        };
      }),
  };
  let serialized = JSON.stringify(context);
  // The API limit applies after JSON escaping; individual text limits do not bound the total.
  // Authored props are also in the full previous spec, so trim those before observations and history.
  while (serialized.length > 30_000) {
    if (context.authoredOrder.length > 0) {
      context.authoredOrder.pop();
      context.authoredOrderTruncated = true;
    } else if (context.renderedOrder.length > 0) {
      context.renderedOrder.pop();
      context.renderedOrderTruncated = true;
    } else if (context.completedTurns?.length) context.completedTurns.shift();
    else break;
    serialized = JSON.stringify(context);
  }
  return serialized;
};

const GOOGLE_FONTS_URL_PATTERN = /https?:\/\/fonts\.googleapis\.com\/[^\s)]+/g;

export const resolvePromptGoogleFont = (
  promptText: string,
): ReturnType<typeof parseGoogleFontsStylesheetUrl> => {
  for (const match of promptText.match(GOOGLE_FONTS_URL_PATTERN) ?? []) {
    const parsed = parseGoogleFontsStylesheetUrl(match);
    if (parsed) return parsed;
  }
  return null;
};

const PASTED_URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_PUNCTUATION_PATTERN = /[).,;:!?]+$/;
const STORED_ASSET_URL_PREFIX = `${dataServiceBaseUrl.replace(/\/$/, "")}/media/`;

/**
 * Pasted http(s) links whose path ends in a PNG, JPEG, WebP or SVG extension, in order and without
 * repeats. Links to our own stored assets are left out, since they are already copied.
 */
export const findPastedImageUrls = (promptText: string): string[] => {
  const urls = (promptText.match(PASTED_URL_PATTERN) ?? []).map((match) =>
    match.replace(TRAILING_PUNCTUATION_PATTERN, ""),
  );
  return [
    ...new Set(
      urls.filter((url) => {
        if (url.startsWith(STORED_ASSET_URL_PREFIX)) return false;
        try {
          return /\.(png|jpe?g|webp|svg)$/i.test(new URL(url).pathname);
        } catch {
          return false;
        }
      }),
    ),
  ];
};

/**
 * Swaps each whole pasted link for its stored URL. Matching whole links keeps `x.png` from
 * rewriting the start of `x.png?w=2`.
 */
export const replacePastedImageUrls = (
  promptText: string,
  storedUrlByPastedUrl: ReadonlyMap<string, string>,
) =>
  promptText.replace(PASTED_URL_PATTERN, (match) => {
    const url = match.replace(TRAILING_PUNCTUATION_PATTERN, "");
    const storedUrl = storedUrlByPastedUrl.get(url);
    return storedUrl == null ? match : storedUrl + match.slice(url.length);
  });

/** Removes Google Fonts embed HTML users paste from Google Fonts "Get embed code". */
export const stripFontEmbedMarkup = (text: string) =>
  text
    .replace(/<link\b[^>]*\bfonts\.googleapis\.com[^>]*>/gi, "")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const BRAND_COLOUR_PATTERNS = [
  /\b(?:brand|primary)\s+colou?r\b[^#]{0,24}(#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}))\b/i,
  /\buse\s+(#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}))\s+for\s+the\s+report\b/i,
];

/** Where an attached image goes: `top` is the banner, `inside` a normal Image in the body. */
export type TemplateStudioImagePlacement = "top" | "inside";

// Phrases about where something goes, not bare nouns: "show the top 5 stories" or "add a
// section" describe the report, not the image.
const TOP_PLACEMENT_PATTERN =
  /\b(banner|masthead|hero image|cover image|header image)\b|\b(at|across|along|on) the top\b|\b(in|as) the header\b/i;
const INSIDE_PLACEMENT_PATTERN =
  /\binside the (report|page|body)\b|\b(in|into|to) (a|an|the|this|that|each|every)( \w+)? section\b|\bin the (body|middle)\b|\bat the bottom\b|\b(below|under|beneath|after) the (header|title|heading|banner)\b/i;

/**
 * The placement the prompt states in words. Null when it states neither or both, so
 * Template Studio asks. "Logo" names no placement: users mean the image itself.
 */
export const resolvePromptImagePlacement = (
  promptText: string,
): TemplateStudioImagePlacement | null => {
  // A pasted link's file name, such as banner.png, says nothing about placement.
  const words = promptText.replace(PASTED_URL_PATTERN, "");
  const saysTop = TOP_PLACEMENT_PATTERN.test(words);
  const saysInside = INSIDE_PLACEMENT_PATTERN.test(words);
  if (saysTop === saysInside) return null;
  return saysTop ? "top" : "inside";
};

export const resolvePromptBrandColor = (promptText: string): string | null => {
  for (const pattern of BRAND_COLOUR_PATTERNS) {
    const match = promptText.match(pattern);
    const hex = match?.[1];
    if (hex != null && isChartHexColor(hex)) return hex;
  }
  return null;
};

export interface TemplateStudioSpecElement {
  type?: string;
  props?: Record<string, unknown>;
}

export interface TemplateStudioSpecSnapshot {
  elements?: Record<string, TemplateStudioSpecElement>;
}

const TEMPLATE_STUDIO_CHART_COMPONENT_TYPES = new Set([
  "AreaChart",
  "BarChart",
  "ComposedChart",
  "HorizontalBar",
  "LineChart",
  "PieChart",
  "RadarChart",
  "RadialBarChart",
  "StackedBarChart",
]);

const CHART_COLOUR_REFINEMENT_PATTERN =
  /(?:\bcolou?rs?\b|#[0-9A-Fa-f]{3,8}\b|\b(?:blue|red|green|orange|purple|violet|gray|grey|teal|rose|yellow|brand)\b)/i;

/** Explicit chart-type swaps only — not "in the pie chart, use blue…". */
const CHART_TYPE_SWAP_PATTERN =
  /\b(change|switch|convert|replace)\b[\s\S]*?\b(?:to|into|with)\b[\s\S]*?\b(?:a\s+)?(?:(?:(?:bar|pie|line|area|radial|radar|stacked|composed)\s+chart)|(?:(?:bar|pie|line|area)\s+chart))\b/i;

/** True when the prompt likely asks to recolour charts, not swap chart types. */
export const isChartColourRefinementPrompt = (prompt: string): boolean =>
  CHART_COLOUR_REFINEMENT_PATTERN.test(prompt) &&
  !CHART_TYPE_SWAP_PATTERN.test(prompt);

/** Chart elements in a layout spec snapshot (for refinement hints). */
export const collectChartElementsFromSpec = (
  spec: TemplateStudioSpecSnapshot | null | undefined,
): { elementId: string; type: string }[] => {
  if (spec?.elements == null) return [];

  return Object.entries(spec.elements).flatMap(([elementId, element]) => {
    const type = element?.type;
    if (type == null || !TEMPLATE_STUDIO_CHART_COMPONENT_TYPES.has(type)) {
      return [];
    }
    return [{ elementId, type }];
  });
};

/**
 * Injects a refinement hint when the user recolours existing charts so the model
 * does not swap PieChart ↔ BarChart (catalog rules alone are often insufficient).
 */
export const buildChartColourRefinementHint = ({
  userPrompt,
  previousSpec,
}: {
  userPrompt: string;
  previousSpec?: TemplateStudioSpecSnapshot | null;
}): string | null => {
  if (previousSpec == null || !isChartColourRefinementPrompt(userPrompt)) {
    return null;
  }

  const charts = collectChartElementsFromSpec(previousSpec);
  if (charts.length === 0) return null;

  const chartList = charts
    .map(({ elementId, type }) => `${elementId} (${type})`)
    .join(", ");

  return `Colour refinement only: keep each chart element's type unchanged (${chartList}). Update only its supported colour fields (\`color\`, \`colors\`, \`series[].color\`, \`dataKeys[].color\`, or \`segments[].color\`) on those same element ids — do not change chart types unless the user explicitly asked for a different chart.`;
};

export const buildGenerateLayoutPrompt = ({
  userPrompt,
  googleFont,
  brandColor,
  previousSpec,
  attachedImageUrls = [],
  handledImageEdit,
}: {
  userPrompt: string;
  googleFont: ReturnType<typeof resolvePromptGoogleFont>;
  brandColor?: string | null;
  previousSpec?: TemplateStudioSpecSnapshot | null;
  /** Stored asset URLs for images the user attached to this message. */
  attachedImageUrls?: readonly string[];
  /** Set when Template Studio did the crop or resize the message asks for; `src` is the edited copy, if it made one. */
  handledImageEdit?: { src?: string };
}) => {
  const cleanedPrompt = stripFontEmbedMarkup(userPrompt);
  const lines = [
    cleanedPrompt.length > 0 ? cleanedPrompt : "Create a report layout.",
  ];

  // Without this, "add a section" on an empty canvas reads as revising a report Herald can't see.
  if (previousSpec == null) {
    lines.push(
      "There is no existing layout yet. Treat requests to add components as creating a new layout that contains only what the user asks for.",
    );
  }

  if (googleFont) {
    lines.push(
      `Typography uses "${googleFont.family}" (loaded outside the spec). Never put <link>, <style>, raw CSS, or font embed snippets in Markdown or any component.`,
    );
  }

  if (brandColor != null) {
    lines.push(
      `Brand colour is ${brandColor}. Use for chart fills when the user did not specify other colours.`,
    );
  }

  const chartColourHint = buildChartColourRefinementHint({
    userPrompt: cleanedPrompt,
    previousSpec,
  });
  if (chartColourHint != null) {
    lines.push(chartColourHint);
  }

  if (attachedImageUrls.length > 0) {
    lines.push(
      [
        "The user attached these uploaded images. Where the request uses an attached image, set the Image src to its exact URL:",
        ...attachedImageUrls.map((url) => `- ${url}`),
        "If the user wants an attached image as a banner or header image, set banner: true on that Image.",
        "Add only what the user asks for. If they only ask to use an attached image as a banner, add just that Image: do not add a header, sections or any other component.",
      ].join("\n"),
    );
  }

  // Without this, Herald also "resizes" the image by changing its maxHeight.
  if (handledImageEdit) {
    lines.push(
      handledImageEdit.src
        ? `Template Studio already did the crop or resize this message asks for: the layout now uses the edited image at ${handledImageEdit.src}. Keep that Image's src, and do not change its maxHeight or size for this request.`
        : "Template Studio is handling the crop or resize this message asks for itself. Do not change any Image for it.",
    );
  }

  return lines.join("\n\n");
};

const heraldSlotContentTypeSet = new Set<string>(
  templateHeraldSlotContentTypes,
);

const templateStudioReportComponentTypes = new Set(
  Object.keys(templateStudioCatalog.data.components),
);

const sortComponentLabels = (labels: string[]) =>
  labels.sort((left, right) => left.localeCompare(right));

const toAvailableComponentLabels = (componentTypes: string[]) =>
  sortComponentLabels(componentTypes.map(formatTemplateStudioComponentLabel));

/** Sorted chip labels for Template Studio report chrome components. */
export const TEMPLATE_STUDIO_REPORT_AVAILABLE_COMPONENTS =
  toAvailableComponentLabels(
    templateStudioRendererComponentTypes.filter(
      (componentType) =>
        templateStudioReportComponentTypes.has(componentType) &&
        // Image is overridden in the report catalog but stays a content chip.
        !heraldSlotContentTypeSet.has(componentType),
    ),
  );

/** Sorted chip labels for user-requestable Herald content in Template Studio. */
export const TEMPLATE_STUDIO_CONTENT_AVAILABLE_COMPONENTS =
  toAvailableComponentLabels(
    templateStudioRendererComponentTypes.filter((componentType) =>
      heraldSlotContentTypeSet.has(componentType),
    ),
  );
